/** Default name of an access request's identity parameter */
export const DEFAULT_WIF_REQUEST_PARAMETER = "principal";

/** Credentials are obtained via AWS workload identity federation */
type AwsWifServerCredential = {
  /** AWS negotiates identity federation */
  csp: "aws";
  /** IAM role ARN to assume when `awsRoleArn` is not set and the grant names
   * no delegated identity */
  pool?: string;
  /** JWT audience for the pool */
  audience?: string;
  /** AWS role ARN template; values will be expanded from access request using
   * LiquidJS, with the request object available as `request`
   *
   * Must render to the ARN of the IAM role to assume.
   */
  awsRoleArn?: string;
};

/** Credentials are obtained via GCP workload identity federation */
type GcpWifServerCredential = {
  /** GCP negotiates identity federation */
  csp: "gcp";
  /** WIF pool provider URI */
  pool?: string;
  /** JWT audience for the pool; the gcp-wif provider sets it at install */
  audience?: string;
};

/** Credentials are obtained by exchanging a token the gateway mints at an
 * RFC 8693 token endpoint; templates are LiquidJS, rendered with the access
 * request as `request` */
type GenericWifServerCredential = {
  /** Any RFC 8693 token endpoint negotiates identity federation */
  csp: "generic";
  /** Template for the token endpoint; must render to an `https://` URL */
  tokenEndpoint: string;
  /** Templates for the RFC 8693 parameters sent with the exchange */
  requestParams?: {
    client_id?: string;
    audience?: string;
    scope?: string;
    resource?: string;
    requested_token_type?: string;
  };
  /** Claims template for the token the gateway mints; must set `aud`, and
   * other claims are limited to the claims that `native` allows */
  claims: string;
};

/** Credentials are obtained via workload identity federation, by the
 * provider in `csp` */
type WifServerCredential = {
  /** Uses workload identity federation */
  type: "wif";
  /** Request parameter that holds the access identity identifier */
  parameter: string;
} & (
  | AwsWifServerCredential
  | GcpWifServerCredential
  | GenericWifServerCredential
);

/** Credentials are obtained by impersonating a service identity the grant
 * provisioned
 *
 * Unlike {@link WifServerCredential}, the upstream sees that identity rather
 * than the federated principal. The identity, pool and subject come from the
 * grant's `generated.impersonate`, so nothing here names them.
 */
type ImpersonationServerCredential = {
  /** Uses an identity the grant provisioned */
  type: "impersonation";
  /** Cloud that mints the impersonated token */
  csp: "gcp";
  /** OAuth scopes the impersonated token carries
   *
   * The gateway refuses scopes spanning every API (e.g. `cloud-platform`), so
   * these name the one product the server fronts.
   */
  scopes: string[];
};

/** Credentials are vaulted in AWS secret manager */
type AwsSmVault = {
  type: "aws";
  account: string;
  region: string;
};

/** Credentials are vaulted in GCP secret manager */
type GsmVault = {
  type: "gcp";
  project: string;
};

/** Credentials are obtained by requesting a secret from a vault */
type VaultCredential = {
  /** Uses vaulted credentials */
  type: "vault";
  /** Vault backend hosting platform
   *
   * Present names the vault to read, for a secret configured directly. Absent
   * means the consumer reads the vault named by the grant that provisioned the
   * secret, so the two cannot disagree.
   */
  hosting?: AwsSmVault | GsmVault; // In future add hashi etc.
  /** JWT audience for the workload identity pool the secret is read through
   *
   * Present names the pool to federate through, for a secret configured
   * directly alongside `hosting`. Absent means the consumer uses the pool named
   * by the grant that provisioned the secret, which follows the same request
   * the vault does.
   */
  audience?: string;
  /** Secret path within host
   *
   * May use parameters from the tool call, including the `location` parameter and the `args` parameter,
   * interpret via nunjucks / jmes syntax.
   */
  path: string;
};

/** The OAuth 2.0 authorization code grant */
type AuthorizationCodeGrant = {
  type: "authorization_code";
  /** Optional scopes to request */
  scope?: string;
};

/** An OAuth grant, discriminated on `type` (currently only
 * `authorization_code`; `client_credentials` is planned) */
type OauthGrant = AuthorizationCodeGrant;

/** Credentials obtained via an OAuth session */
type OauthCredential = {
  /** Uses an OAuth credential */
  type: "oauth";
  /** The OAuth grant used to obtain the credential */
  grant: OauthGrant;
};

/** Credential uses the natively minted gateway session token, directly */
type NativeCredential = {
  /** Uses gateway-minted session token */
  type: "native";
  /** Claims template; values will be expanded from access request using LiquidJS
   *
   * Must be a string value that JSON parses as a JWTPayload object.
   */
  claims?: string;
};

/** Credentials obtained by minting a short-lived AWS RDS IAM
 * database-authentication token */
type RdsIamCredential = {
  /** Uses an AWS RDS IAM auth token as the database password */
  type: "rds-iam";
  /** LiquidJS template for the grant-provisioned IAM role ARN; no fallback role */
  awsRoleArn: string;
  /** JWT audience required by the selected AWS identity provider */
  audience?: string;
  /** LiquidJS template for the AWS RDS endpoint hostname, rendered with the approved request as `request` */
  hostname: string;
  /** Optional LiquidJS template for the database port. Omit to use 5432. The rendered value must be an integer from 1 to 65535. */
  port?: string;
  /** LiquidJS template for the database user the IAM token authenticates as, rendered with the approved request as `request` */
  dbUser: string;
  /** LiquidJS template for the database name, rendered with the approved request as `request` */
  database: string;
  /** LiquidJS template for the AWS region hosting the database, rendered with the approved request as `request` */
  region: string;
};

/** How credentials are obtained to connect to an upstream backend */
export type AgenticServerCredential =
  | ImpersonationServerCredential
  | NativeCredential
  | OauthCredential
  | RdsIamCredential
  | VaultCredential
  | WifServerCredential;
